import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { goalsApi, initiativesApi, unitGoalsApi } from '../services/apiService';
import { StrategicGoalList, InitiativeList, UnitGoalList } from '../types';
import { COLORS, FONTS } from '../constants/config';
import { MainStackParamList } from '../navigation/MainNavigator';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getTextAlign } from '../utils/rtl';

import { tokens } from '@design';
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const SearchCheckIcon = ({ size = 40, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M11 18C14.866 18 18 14.866 18 11C18 7.13401 14.866 4 11 4C7.13401 4 4 7.13401 4 11C4 14.866 7.13401 18 11 18Z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path d="M16 16L20 20" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M8 11L10 13L14 9" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const ChevronLeftIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);


// ============================================================
// Props Interface
// ============================================================

interface PendingApprovalsCardProps {
  organizationId: number;
  onApprove?: (type: 'goal' | 'initiative' | 'unitGoal', id: number) => Promise<void>;
  onReject?: (type: 'goal' | 'initiative' | 'unitGoal', id: number) => Promise<void>;
}

// ============================================================
// Main Component
// ============================================================

export default function PendingApprovalsCard({ organizationId, onApprove, onReject }: PendingApprovalsCardProps) {
  const navigation = useNavigation<NavigationProp>();
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'goals' | 'initiatives' | 'unitGoals'>('goals');
  const [loading, setLoading] = useState(true);
  const [pendingGoals, setPendingGoals] = useState<StrategicGoalList[]>([]);
  const [pendingInitiatives, setPendingInitiatives] = useState<InitiativeList[]>([]);
  const [pendingUnitGoals, setPendingUnitGoals] = useState<UnitGoalList[]>([]);

  // Theme colors
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textMuted: colors.textMuted,
    backgroundAlt: colors.backgroundAlt,
    iconDefault: colors.iconDefault,
    tabInactiveText: isDark ? tokens.colors.raw.gray["400"] : tokens.colors.raw.slate["700"],
  };

  const fetchPendingItems = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch pending goals, initiatives, and unit goals for this organization
      const [goalsResponse, initiativesResponse, unitGoalsResponse] = await Promise.all([
        goalsApi.list({ department_id: organizationId, status: 'pending' }),
        initiativesApi.list({ organization: organizationId, approval_status: 'pending' }),
        unitGoalsApi.list({ approval_status: 'pending' }),
      ]);

      setPendingGoals(goalsResponse.results || []);
      setPendingInitiatives(initiativesResponse.results || []);
      setPendingUnitGoals(unitGoalsResponse.results || []);
    } catch (error) {
      console.error('Error fetching pending items:', error);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchPendingItems();
  }, [fetchPendingItems]);

  const handleViewGoalDetails = (goalId: number) => {
    navigation.navigate('GoalDetail', { id: goalId });
  };

  const handleViewInitiativeDetails = (initiativeId: number) => {
    navigation.navigate('InitiativeDetail', { id: initiativeId });
  };

  const handleViewUnitGoalDetails = (unitGoalId: number) => {
    navigation.navigate('UnitGoalDetail', { id: unitGoalId });
  };

  const currentItems = activeTab === 'goals' ? pendingGoals : activeTab === 'initiatives' ? pendingInitiatives : pendingUnitGoals;
  const totalPending = pendingGoals.length + pendingInitiatives.length + pendingUnitGoals.length;

  // Don't render if no pending items
  if (!loading && totalPending === 0) {
    return null;
  }

  return (
    <View style={[styles.container, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <Text style={[styles.headerTitle, { color: themeColors.textPrimary }]}>{t('approvals.pendingGoalsAndInitiatives')}</Text>
        </View>
        <SearchCheckIcon size={40} color={themeColors.iconDefault} />
      </View>

      {/* Tabs */}
      <View style={[styles.tabsContainer, { borderBottomColor: themeColors.cardBorder }]}>
        <View style={styles.tabsRow}>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'unitGoals' && styles.tabInactive]}
            onPress={() => setActiveTab('unitGoals')}
          >
            <Text style={[styles.tabText, activeTab === 'unitGoals' && { color: themeColors.tabInactiveText }]}>
              {t('approvals.unitGoalsForApproval')}
              {pendingUnitGoals.length > 0 && ` (${pendingUnitGoals.length})`}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'initiatives' && styles.tabInactive]}
            onPress={() => setActiveTab('initiatives')}
          >
            <Text style={[styles.tabText, activeTab === 'initiatives' && { color: themeColors.tabInactiveText }]}>
              {t('approvals.initiativesForApproval')}
              {pendingInitiatives.length > 0 && ` (${pendingInitiatives.length})`}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'goals' && styles.tabActive]}
            onPress={() => setActiveTab('goals')}
          >
            <Text style={[styles.tabText, activeTab === 'goals' && styles.tabTextActive]}>
              {t('approvals.goalsForApproval')}
              {pendingGoals.length > 0 && ` (${pendingGoals.length})`}
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Content Area */}
      <View style={[styles.contentArea, { backgroundColor: themeColors.backgroundAlt }]}>
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="small" color="#0F9CD8" />
          </View>
        ) : currentItems.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={[styles.emptyText, { color: themeColors.textMuted }]}>
              {activeTab === 'goals'
                ? t('approvals.noGoalsPending')
                : activeTab === 'initiatives'
                ? t('approvals.noInitiativesPending')
                : t('approvals.noUnitGoalsPending')}
            </Text>
          </View>
        ) : (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.cardsScrollContent}
            style={styles.cardsScrollView}
          >
            {activeTab === 'goals'
              ? pendingGoals.map((goal) => (
                  <View key={goal.id} style={[styles.itemCard, { backgroundColor: themeColors.card }]}>
                    {/* Card Title */}
                    <Text style={[styles.cardTitle, { color: themeColors.textPrimary }]} numberOfLines={2}>
                      {goal.title}
                    </Text>

                    {/* Status Badge */}
                    <View style={styles.statusBadge}>
                      <Text style={styles.statusBadgeText}>{t('approvals.pendingApproval')}</Text>
                    </View>

                    {/* View Details Link */}
                    <TouchableOpacity
                      style={styles.viewDetailsLink}
                      onPress={() => handleViewGoalDetails(goal.id)}
                    >
                      <ChevronLeftIcon size={16} color={themeColors.textMuted} />
                      <Text style={[styles.viewDetailsText, { color: themeColors.textMuted }]}>{t('common.viewDetails')}</Text>
                    </TouchableOpacity>
                  </View>
                ))
              : activeTab === 'initiatives'
              ? pendingInitiatives.map((initiative) => (
                  <View key={initiative.id} style={[styles.itemCard, { backgroundColor: themeColors.card }]}>
                    {/* Card Title */}
                    <Text style={[styles.cardTitle, { color: themeColors.textPrimary }]} numberOfLines={2}>
                      {initiative.title}
                    </Text>

                    {/* Status Badge */}
                    <View style={styles.statusBadge}>
                      <Text style={styles.statusBadgeText}>{t('approvals.pendingApproval')}</Text>
                    </View>

                    {/* View Details Link */}
                    <TouchableOpacity
                      style={styles.viewDetailsLink}
                      onPress={() => handleViewInitiativeDetails(initiative.id)}
                    >
                      <ChevronLeftIcon size={16} color={themeColors.textMuted} />
                      <Text style={[styles.viewDetailsText, { color: themeColors.textMuted }]}>{t('common.viewDetails')}</Text>
                    </TouchableOpacity>
                  </View>
                ))
              : pendingUnitGoals.map((unitGoal) => (
                  <View key={unitGoal.id} style={[styles.itemCard, { backgroundColor: themeColors.card }]}>
                    {/* Card Title */}
                    <Text style={[styles.cardTitle, { color: themeColors.textPrimary }]} numberOfLines={2}>
                      {unitGoal.name}
                    </Text>

                    {/* Status Badge */}
                    <View style={styles.statusBadge}>
                      <Text style={styles.statusBadgeText}>{t('unitGoals.pendingApproval')}</Text>
                    </View>

                    {/* View Details Link */}
                    <TouchableOpacity
                      style={styles.viewDetailsLink}
                      onPress={() => handleViewUnitGoalDetails(unitGoal.id)}
                    >
                      <ChevronLeftIcon size={16} color={themeColors.textMuted} />
                      <Text style={[styles.viewDetailsText, { color: themeColors.textMuted }]}>{t('common.viewDetails')}</Text>
                    </TouchableOpacity>
                  </View>
                ))}
          </ScrollView>
        )}
      </View>
    </View>
  );
}

// ============================================================
// Styles
// ============================================================

const getStyles = (isRTL: boolean) => StyleSheet.create({
  container: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["5"],
    gap: tokens.spacing.absolute["5"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
  },
  headerContent: {
    flex: 1,
    alignItems: 'flex-end',
  },
  headerTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },

  // Tabs
  tabsContainer: {
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["200"],
    paddingVertical: tokens.spacing.absolute["3"],
  },
  tabsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  tab: {
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: 7.5,
    borderRadius: tokens.radii.absolute["radius-8"],
    minHeight: 36,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tabActive: {
    backgroundColor: '#0F9CD8',
  },
  tabInactive: {
    backgroundColor: 'transparent',
  },
  tabText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    textAlign: 'center',
  },
  tabTextActive: {
    color: COLORS.white,
  },
  tabTextInactive: {
    color: tokens.colors.raw.slate["700"],
  },

  // Content Area
  contentArea: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
    minHeight: 200,
  },
  cardsScrollView: {
    transform: [{ scaleX: -1 }],
  },
  cardsScrollContent: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["4"],
    paddingHorizontal: tokens.spacing.absolute["1"],
    paddingVertical: tokens.spacing.absolute["1"],
  },

  // Item Card - Simple Figma Design
  itemCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
    width: 240,
    gap: tokens.spacing.absolute["3"],
    transform: [{ scaleX: -1 }], // Flip back to normal (parent is flipped for RTL scroll)
  },

  // Card Title
  cardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 22,
  },

  // Status Badge
  statusBadge: {
    backgroundColor: '#9467BD',
    borderRadius: 40,
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    alignSelf: 'flex-start',
  },
  statusBadgeText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: COLORS.white,
  },

  // View Details Link
  viewDetailsLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["1"],
  },
  viewDetailsText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },

  // Loading & Empty States
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 160,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 160,
  },
  emptyText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    textAlign: 'center',
  },
});

const styles = getStyles(true);
